// src/components/homepage/PipelineVote.jsx
//
// Ticket 26 part B. Thumbs up / thumbs down on a "Popular -> Hidden Gem" card,
// for a reader who has read both books. One tap votes; tapping the same thumb
// again takes the vote back; tapping the other one changes it.
//
// Who may vote is the database's call (pipeline_vote_state tells this card, and
// cast_pipeline_vote enforces it). A reader who can't vote sees the same two
// thumbs with the reason beside them, so the card has the same shape for
// everyone and the reason is never a surprise. Counts are never shown to
// readers; only admins see them (admin Pipelines).
//
// Two kinds of reader who can't vote yet. A signed-out reader's thumbs stay
// tappable: a tap opens the sign-up pop-up through requireAuth (the page's
// gate.requireAuth, as Save and Already Read do) and casts nothing; once they
// sign in the state is read again and the thumbs show what they may do. A
// signed-in reader who hasn't read both books keeps greyed thumbs that do
// nothing.
//
// If the state can't be read, the strip draws nothing rather than a greyed
// thumb with a wrong reason. A vote that fails to save goes back to what it was
// and says so.
//
// The parent renders this with key={pipelineId}, so each pipeline gets its own
// fresh state when the reader moves to the next card.

import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '../../lib/supabase';
import { subscribeToAuth } from '../../lib/auth';
import { nextVote, tapAction, voteHint, reasonFromError, VOTE_UP, VOTE_DOWN } from '../../lib/pipelineVote';

function Thumb({ down }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         style={down ? { transform: 'rotate(180deg)' } : undefined}>
      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
    </svg>
  );
}

export default function PipelineVote({ pipelineId, requireAuth }) {
  const [state, setState] = useState(null);        // { canVote, reason, myVote } once read
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [authTick, setAuthTick] = useState(0);     // bumped on sign-in / sign-out

  useEffect(() => subscribeToAuth(() => setAuthTick(t => t + 1)), []);

  useEffect(() => {
    let cancelled = false;
    setState(null);
    setFailed(false);
    setMessage('');
    (async () => {
      const { data, error } = await supabase.rpc('pipeline_vote_state', { p_pipeline_ids: [pipelineId] });
      if (cancelled) return;
      if (error || !Array.isArray(data) || data.length === 0) {
        console.error('pipeline_vote_state failed:', error?.message || 'no row came back');
        setFailed(true);
        return;
      }
      const row = data[0];
      setState({ canVote: row.can_vote === true, reason: row.reason ?? null, myVote: Number(row.my_vote) || 0 });
    })();
    return () => { cancelled = true; };
  }, [pipelineId, authTick]);

  const action = tapAction(state, typeof requireAuth === 'function');

  async function press(pressed) {
    if (busy) return;
    if (action === 'sign_in') { requireAuth(); return; }   // open the sign-up pop-up; no vote is cast
    if (action !== 'vote') return;
    const before = state.myVote;
    const target = nextVote(before, pressed);
    setBusy(true);
    setMessage('');
    setState(s => ({ ...s, myVote: target }));      // show it at once; put it back if it fails
    const { data, error } = await supabase.rpc('cast_pipeline_vote', { p_pipeline_id: pipelineId, p_vote: target });
    setBusy(false);
    if (!error && Number(data) === target) return;
    const reason = reasonFromError(error);
    if (reason) {
      setState({ canVote: false, reason, myVote: before });
      return;
    }
    console.error('cast_pipeline_vote failed:', error?.message || `stored ${data}, wanted ${target}`);
    Sentry.captureException(error || new Error(`pipeline vote not stored: wanted ${target}, got ${data}`), { tags: { area: 'pipeline_vote' } });
    setState(s => ({ ...s, myVote: before }));
    setMessage('Couldn’t save your vote. Try again.');
  }

  if (failed) return null;

  const base = {
    width: 40, height: 40, borderRadius: 'var(--radius-circle)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: 0, flexShrink: 0, transition: 'background 0.15s, color 0.15s',
  };
  const thumb = (vote, label, activeBg) => {
    const on = state?.myVote === vote;
    const can = action !== 'none';     // 'vote' or 'sign_in': the thumb is live
    return (
      <button
        type="button"
        onClick={() => press(vote)}
        disabled={!can || busy}
        aria-pressed={on}
        aria-label={label}
        title={action === 'vote' ? label : action === 'sign_in' ? 'Sign in to vote' : voteHint(state?.reason)}
        style={{
          ...base,
          border: `2px solid ${on ? activeBg : 'var(--brand-sand)'}`,
          background: on ? activeBg : 'var(--brand-white)',
          color: on ? 'var(--brand-white)' : 'var(--neutral-600)',
          cursor: can && !busy ? 'pointer' : 'default',
          opacity: can ? 1 : 0.45,
        }}
      >
        <Thumb down={vote === VOTE_DOWN} />
      </button>
    );
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 'var(--space-10)', flexWrap: 'wrap', minHeight: 60,
      padding: '10px 16px', background: 'var(--brand-paper)', borderTop: '1px solid var(--brand-cream)',
    }}>
      {state && (
        <>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-600)', fontWeight: 600 }}>
            Does this pairing work?
          </span>
          <div style={{ display: 'flex', gap: 'var(--space-6)' }}>
            {thumb(VOTE_UP, 'Yes, this pairing works', 'var(--brand-forest)')}
            {thumb(VOTE_DOWN, 'No, this pairing doesn’t work', 'var(--brand-rust)')}
          </div>
          {!state.canVote && (
            <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-500)' }}>{voteHint(state.reason)}</span>
          )}
          {message && (
            <span role="alert" style={{ fontSize: 'var(--font-size-xxs)', color: '#991b1b' }}>{message}</span>
          )}
        </>
      )}
    </div>
  );
}
